
import { useNavigate } from "react-router-dom";

import Map, { getBoundsForEntity } from '../Map';
import { Download, Maximize2, MapPin } from 'lucide-react';
import { ResourceDisclosureHeader } from './ResourceDisclosure';
import { Button } from '@/components/ui/button';
import { useAtom } from 'jotai';
import { authenticationAtom, mapQueryAtom } from "../State";
import { mapDataAtom } from "../State";
import { mapOptionsAtom } from "../State";
import { selectedEntityAtom } from "../State";


import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { userAPI } from '../UserAPI';
import { Building, LocalGovernment, Area, PoIType, getEntityType } from "@the-real-insight/tri-model";
import markerAddress from '../components/map/marker-address-icon.svg';
import markerChargingStation from '../components/map/marker-charging-station-icon.svg';
import markerUniversity from '../components/map/marker-university-icon.svg';
import markerSchool from '../components/map/marker-school-icon.svg';
import markerFireStation from '../components/map/marker-fire-station-icon.svg';
import markerBillboard from '../components/map/marker-billboard-icon.svg';
import markerChildCareFacility from '../components/map/marker-child-care-facility-icon.svg';
import markerHospital from '../components/map/marker-hospital-icon.svg';
import markerPharmacy from '../components/map/marker-pharmacy-icon.svg';
import markerRestaurant from '../components/map/marker-restaurant-icon.svg';
import markerHotel from '../components/map/marker-hotel-icon.svg';
import markerAirport from '../components/map/marker-airport-icon.svg';
import markerPoliceStation from '../components/map/marker-police-station-icon.svg';
import markerFreewayMaintenanceOffice from '../components/map/marker-freeway-maintenance-office-icon.svg';
import markerParkingLot from '../components/map/marker-parking-lot-icon.svg';
import markerBusStop from '../components/map/marker-bus-stop-icon.svg';
import markerChurch from '../components/map/marker-church-icon.svg';
import markerHairdresser from '../components/map/marker-hairdresser-icon.svg';
import markerSupermarket from '../components/map/marker-supermarket-icon.svg';
import { brandMarkerUrl } from '../components/map/brandMarkers';
import markerDoctor from '../components/map/marker-doctor-icon.svg';
import markerParcelStation from '../components/map/marker-parcel-station-icon.svg';
import markerCareFacility from '../components/map/marker-care-facility-icon.svg';
import markerCompany from '../components/map/marker-company-icon.svg';
import markerDepartment from '../components/map/marker-department-icon.svg';
import markerCandidateProject from '../components/map/marker-candidate-project-icon.svg';
import markerTrainStation from '../components/map/marker-train-station-icon.svg';
import markerPowerUnit from '../components/map/marker-power-unit-icon.svg';
import markerCellularTower from '../components/map/marker-cellular-tower-icon.svg';
import markerAdvertisementColumn from '../components/map/marker-advertisement-column.svg';
import markerPollution from '../components/map/marker-pollution-icon.svg';
import markerBiomassUnit from '../components/map/marker-biomassUnit-icon.svg';
import markerGasConsumerUnit from '../components/map/marker-gasConsumerUnit-icon.svg';
import markerGasProducerUnit from '../components/map/marker-gasProducerUnit-icon.svg';
import markerGasStorageUnit from '../components/map/marker-gasStorageUnit-icon.svg';
import markerGeoGrubengasPressureReliefUnit from '../components/map/marker-geoGrubengasPressureReliefUnit-icon.svg';
import markerNuclearUnit from '../components/map/marker-nuclearUnit-icon.svg';
import markerSolarUnit from '../components/map/marker-solarUnit-icon.svg';
import markerSubstation from '../components/map/marker-substation-icon.svg';
import leafletImage from 'leaflet-image';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import tinycolor from 'tinycolor2';
import { configuration } from '../Configuration';

// CRITICAL: Prevent leaflet-image crash with L.divIcon markers
// leaflet-image tries to access marker._icon.src which is undefined for divIcons,
// and then calls .match() on it, causing a crash.
const originalMarkerOnAdd = L.Marker.prototype.onAdd;
L.Marker.prototype.onAdd = function (this: L.Marker, map: L.Map) {
    const result = originalMarkerOnAdd.call(this, map);
    if (this.getElement()) {
        const el = this.getElement() as any;
        if (el && el.tagName === 'DIV' && !el.src) {
            // Set a dummy src so leaflet-image doesn't crash
            el.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        }
    }
    return result;
};

// Shared rendering function that uses the proven download logic
const renderMapForCapture = async (mapResource: any): Promise<{dataURL: string, cleanup: () => void} | null> => {
    
    // Create a temporary container for the map
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '-9999px';
    tempContainer.style.width = '800px';
    tempContainer.style.height = '600px';
    tempContainer.style.visibility = 'hidden';
    document.body.appendChild(tempContainer);

    if (!L || !L.map) {
        console.error('🗺️ [SHARED] Leaflet not properly imported');
        document.body.removeChild(tempContainer);
        return null;
    }

    const tempMap = L.map(tempContainer, {
        preferCanvas: true,
        zoomControl: false,
        attributionControl: false,
        dragging: false,
        touchZoom: false,
        doubleClickZoom: false,
        scrollWheelZoom: false,
        boxZoom: false,
        keyboard: false,
        zoomAnimation: false,
        fadeAnimation: false,
        markerZoomAnimation: false
    });
    
    console.log('🗺️ [SHARED] Map created, container size:', tempContainer.offsetWidth, 'x', tempContainer.offsetHeight);

    const cleanup = () => {
        try {
            tempMap.remove();
            document.body.removeChild(tempContainer);
        } catch (cleanupError) {
            console.warn('🗺️ [SHARED] Error during cleanup:', cleanupError);
        }
    };

    try {
        const tileUrl = 'https://api.mapbox.com/styles/v1/marcgille/clxgwajhc008701pcewze3l4s/tiles/256/{z}/{x}/{y}@2x?access_token=pk.eyJ1IjoibWFyY2dpbGxlIiwiYSI6ImNsZWlvazNkYTAzaWYzb25yZjBmejB5NmMifQ.XF9Z60UyGqXLztoIjH1SIA';
        const tileLayer = L.tileLayer(tileUrl);
        tileLayer.addTo(tempMap);
        
        tileLayer.on('tileerror', (e) => console.error('🗺️ [SHARED] Tile error:', e));

        if (mapResource.options?.bounds) {
            tempMap.fitBounds(mapResource.options.bounds);
        } else {
            tempMap.setView([50.10495071520639, 8.670636045048122], 15);
        }

        const data = mapResource.data;
        
        // Handle all possible map data types (like the original Map component)
        if (data) {
            // Handle new structured layers
            const layers = data.layers;
            const hasNewLayers = !!layers;

            if (layers) {
                Object.values(layers).forEach((layer: any) => {
                    if (layer?.elements) {
                        layer.elements.forEach((element: any) => {
                            addLayerElementToMap(element, tempMap, mapResource.options);
                        });
                    }
                });
            }

            if (!hasNewLayers) {
                // Add parcels
                if (data.parcels && Array.isArray(data.parcels)) {
                    data.parcels.forEach((parcel: any, _index: number) => {
                        addGeometryToMap(parcel, tempMap, mapResource.options);
                    });
                }
                
                // Convert plotsOfLand to parcels if needed (server compatibility)
                if (data.plotsOfLand && Array.isArray(data.plotsOfLand)) {
                    if (!data.parcels) {
                        data.parcels = [];
                    }
                    data.plotsOfLand.forEach((plot: any) => {
                        // Convert PlotOfLand to Parcel format
                        const parcel = {
                            ...plot,
                            _type: 'Parcel',
                            geometry: plot.shape ? {
                                type: 'Polygon',
                                coordinates: [plot.shape.map((point: any) => [point.lon, point.lat])]
                            } : plot.geometry
                        };
                        data.parcels.push(parcel);
                    });
                }
                
                // Add buildings
                if (data.buildings && Array.isArray(data.buildings)) {
                    data.buildings.forEach((building: any) => {
                        addGeometryToMap(building, tempMap, mapResource.options);
                    });
                }
                
                // Add local governments
                if (data.localGovernments && Array.isArray(data.localGovernments)) {
                    data.localGovernments.forEach((localGov: any) => {
                        addGeometryToMap(localGov, tempMap, mapResource.options);
                    });
                }
                
                // Add areas
                if (data.areas && Array.isArray(data.areas)) {
                    data.areas.forEach((area: any) => {
                        addGeometryToMap(area, tempMap, mapResource.options);
                    });
                }
                
                // Add circles (for earthquakes, etc.)
                if (data.circles && Array.isArray(data.circles)) {
                    data.circles.forEach((circle: any) => {
                        const circleMarker = L.circle([circle.lat, circle.lon], {
                            radius: circle.radius * 1000, // Convert km to meters
                            color: circle.color || '#ff0000',
                            fillColor: circle.fillColor || circle.color || '#ff0000',
                            fillOpacity: circle.fillOpacity || 0.3,
                            weight: circle.weight || 2,
                            opacity: circle.opacity || 0.8
                        });
                        
                        if (circle.tooltip) {
                            circleMarker.bindTooltip(circle.tooltip);
                        }
                        
                        circleMarker.addTo(tempMap);
                    });
                }

                // Add pois (legacy support)
                if (data.pois && Array.isArray(data.pois)) {
                    data.pois.forEach((poi: any) => {
                        if (poi.lat !== undefined && poi.lon !== undefined) {
                            const poiType = poi.poiType || poi.type || poi.class;
                            const iconUrl = brandMarkerUrl(poi.brandWikidata) ?? getIconUrl(poiType);
                            const borderColor = getBorderColor(poiType);
                            const poiIcon = L.divIcon({
                                className: 'marker-div-icon',
                                html: `
                                    <div class="marker" style="width: 35px; height: 35px;">
                                        <span class="pin ${String(poiType || '').replace(/[^a-zA-Z0-9]/g, '')}" style="border: 0.5px solid ${borderColor};">
                                            <img src="${iconUrl}" style="width: 25px; height: 25px;" />
                                        </span>
                                    </div>
                                `,
                        iconSize: [35, 35],
                        iconAnchor: [17.5, -1.29],
                        popupAnchor: [0, -35]
                    });
                            L.marker([poi.lat, poi.lon], { icon: poiIcon }).addTo(tempMap);
                        }
                    });
                }
            }
        }

        // Add legend to shadow map
        addLegendToMap(tempMap, mapResource.options);

        // Log summary of what was added to the map
        const _layerCount = (tempMap as any)._layers ? Object.keys((tempMap as any)._layers).length : 0;
        const _mapBounds = tempMap.getBounds();

        // Use simple timeout approach like working download button
        await new Promise(resolve => setTimeout(resolve, 2000));
        tempMap.invalidateSize();
        await new Promise(resolve => setTimeout(resolve, 1000)); // Extra delay for legend rendering
        
        return new Promise((resolve, reject) => {
            
            leafletImage(tempMap, async (err: any, canvas: HTMLCanvasElement) => {

                if (err) {
                    console.error('❌ [SHARED] Error capturing map:', err);
                    cleanup();
                    reject(err);
                    return;
                }

                console.log('✅ [SHARED] Map image captured successfully, size:', canvas.width, 'x', canvas.height);
                
                // Check if canvas has content
                const ctx = canvas.getContext('2d');
                const imageData = ctx?.getImageData(0, 0, canvas.width, canvas.height);
                const hasContent = imageData && Array.from(imageData.data).some(pixel => pixel !== 0);
                console.log('✅ [SHARED] Canvas has content:', hasContent);
                
                const dataURL = canvas.toDataURL('image/png');
                console.log('✅ [SHARED] Data URL generated, length:', dataURL.length);
                
                cleanup();
                resolve({ dataURL, cleanup });
            });
        });
        
    } catch (error) {
        console.error('❌ [SHARED] Error in Map rendering:', error);
        cleanup();
        return null;
    }
};

// Helper function to get parcel styling (moved up for shared use)
const getParcelStyle = (options: any, parcel: any) => {
    // Special styling for buildings (fitted shapes) - make them red for easy identification
    if (parcel.type === 'fittedShape' || parcel.landUse === 'Building') {
        return {
            color: '#87CEEB', // Light blue border
            fillColor: '#87CEEB', // Light blue fill
            weight: 3, // Thicker border for visibility
            fill: true,
            fillOpacity: 0.6, // More opaque for better visibility
        }
    }
    
    if (options?.heatmapField) {
        return {
            color: 'black',
            fillColor: options.heatmapField ? percentToColor(parcel[options.heatmapField] / (options.heatmapField?.heatmapMax || 100) * 100).toString() : '#649aaa',
            weight: 1,
            fill: true,
            fillOpacity: 0.3,
        }
    } else if (options && options.categoryField) {
        const entry = options.categoryField.values.find((entry: any) => entry.key === parcel[options.categoryField.field]);
        return {
            color: entry ? entry.color : '#649aaa',
            fillColor: entry ? entry.color : '#649aaa',
            weight: 1,
            fill: true,
            fillOpacity: 0.3,
        }
    } else {
        return {
            color: '#649aaa',
            fillColor: '#649aaa',
            weight: 1,
            fill: true,
            fillOpacity: 0.3,
        }
    }
};

// Helper function for color calculation
const percentToColor = (value: number) => {
    const minimumColor = tinycolor('#00FF00');
    const maximumColor = tinycolor('#FF0000');
    const step = (maximumColor.toHsl().h - minimumColor.toHsl().h) / 100;
    const color = minimumColor.toHsl();
    color.h += step * value;
    return tinycolor(color);
};

// Helper function to add legend to shadow map
const addLegendToMap = (map: any, options: any) => {
    if (!options) {
        console.log('🗺️ [LEGEND] No options provided');
        return;
    }
    
    console.log('🗺️ [LEGEND] Creating legend with options:', {
        hasHeatmapField: !!options.heatmapField,
        hasHeatmap: !!options.heatmap,
        hasCategoryField: !!options.categoryField,
        legendTitle: options.legend?.title
    });
    
    // Create legend container - match interactive map exactly
    const legendContainer = document.createElement('div');
    legendContainer.className = 'legend';
    // Position it like the interactive map (left: 30px, bottom: 30px)
    legendContainer.style.cssText = 'left: 30px; bottom: 30px;';
    
    // Add title - match interactive map exactly
    const title = document.createElement('div');
    title.className = 'title';
    title.textContent = options.legend?.title || 'Legende';
    legendContainer.appendChild(title);
    
    // Add legend content based on type
    if (options.heatmapField || options.heatmap) {
        addHeatmapLegend(legendContainer, options);
    } else if (options.categoryField) {
        addCategoryLegend(legendContainer, options);
    }
    
    // Skip legend for now - leaflet-image has issues with divIcons
    console.log('🗺️ [LEGEND] Skipping legend due to leaflet-image compatibility issues');
};

// Helper function to add heatmap legend
const addHeatmapLegend = (container: HTMLElement, options: any) => {
    const content = document.createElement('div');
    content.style.cssText = 'display: grid; grid-template-columns: 1fr; gap: 4px;';
    
    if (options.heatmap?.categories) {
        options.heatmap.categories.forEach((category: any) => {
            const item = document.createElement('div');
            item.style.cssText = 'display: flex; align-items: center;';
            
            const colorBox = document.createElement('div');
            colorBox.style.cssText = `width: 20px; height: 20px; background-color: ${category.color}; margin-right: 8px;`;
            
            const label = document.createElement('div');
            label.textContent = category.label;
            
            item.appendChild(colorBox);
            item.appendChild(label);
            content.appendChild(item);
        });
    }
    
    container.appendChild(content);
};

// Helper function to add category legend - match interactive map exactly
const addCategoryLegend = (container: HTMLElement, options: any) => {
    const content = document.createElement('div');
    content.className = 'marginTopM';
    
    // Match the interactive map's grid layout exactly
    const gridColumns = options.categoryField.values.length > 8 ? 
        `repeat(${Math.ceil(options.categoryField.values.length / 8)}, 1fr)` : '1fr';
    
    content.style.cssText = `
        display: grid;
        grid-template-columns: ${gridColumns};
        gap: 4px;
        align-items: start;
        grid-auto-flow: row;
        grid-template-rows: repeat(8, auto);
    `;
    
    if (options.categoryField?.values) {
        options.categoryField.values.forEach((value: any, _index: number) => {
            const item = document.createElement('div');
            item.className = 'displayFlex alignItemsCenter';
            
            const colorBox = document.createElement('div');
            colorBox.className = 'width20 height20';
            colorBox.style.backgroundColor = value.color;
            
            const label = document.createElement('div');
            label.className = 'marginLeftS';
            label.textContent = value.label;
            
            item.appendChild(colorBox);
            item.appendChild(label);
            content.appendChild(item);
        });
    }
    
    container.appendChild(content);
};

// Idempotent geometry conversion function that handles both shape and geometry formats
const normalizeGeometry = (item: any): { type: string; coordinates: any[] } | null => {
    if (!item) {
        return null;
    }
    
    // Case 1: Already has proper geometry format (idempotent)
    if (item.geometry && item.geometry.coordinates && item.geometry.type) {
        return {
            type: item.geometry.type,
            coordinates: item.geometry.coordinates
        };
    }
    
    // Case 2: PlotOfLand with shape property - convert to geometry format
    if (item.shape && Array.isArray(item.shape) && item.shape.length > 0) {
        // Validate shape points
        const validPoints = item.shape.filter((point: any) =>
            point && typeof point.lat === 'number' && typeof point.lon === 'number' &&
            !isNaN(point.lat) && !isNaN(point.lon)
        );

        if (validPoints.length > 2) {
            // Convert shape to GeoJSON polygon format [lon, lat] coordinates
            const coordinates = validPoints.map((point: any) => [point.lon, point.lat]);
            // Close the polygon if not already closed
            if (coordinates[0][0] !== coordinates[coordinates.length - 1][0] ||
                coordinates[0][1] !== coordinates[coordinates.length - 1][1]) {
                coordinates.push(coordinates[0]);
            }

            return {
                type: 'Polygon',
                coordinates: [coordinates] // GeoJSON Polygon format
            };
        }
    }
    
    // Case 3: Legacy format with coordinates array directly on item
    if (item.coordinates && Array.isArray(item.coordinates)) {
        return {
            type: item.type || 'Polygon',
            coordinates: item.coordinates
        };
    }

    return null;
};

// Helper function to add any geometry type to map
const addGeometryToMap = (item: any, map: any, options: any) => {
    // Debug logging for buildings/fitted shapes
    if (item.type === 'fittedShape' || item.landUse === 'Building') {
        console.log('🏢 [BUILDING] Processing building:', {
            name: item.name,
            type: item.type,
            landUse: item.landUse,
            hasGeometry: !!item.geometry,
            geometryType: item.geometry?.type,
            coordinates: item.geometry?.coordinates
        });
    }
    
    // Use the conversion function to normalize geometry
    const normalizedGeometry = normalizeGeometry(item);
    if (!normalizedGeometry) {
        console.warn('🗺️ Invalid geometry data after normalization:', {
            hasItem: !!item,
            hasGeometry: !!item?.geometry,
            hasShape: !!item?.shape,
            hasCoordinates: !!item?.geometry?.coordinates,
            item: item
        });
        return;
    }
    
    if (normalizedGeometry.type === 'Polygon') {
        // Validate polygon coordinates structure
        if (!Array.isArray(normalizedGeometry.coordinates) || normalizedGeometry.coordinates.length === 0) {
            console.warn('🗺️ Invalid polygon coordinates array:', normalizedGeometry.coordinates);
            return;
        }
        
        const coordinates = normalizedGeometry.coordinates[0];
        if (!Array.isArray(coordinates) || coordinates.length === 0) {
            console.warn('🗺️ Invalid polygon coordinate array:', coordinates);
            return;
        }
        
        const coords: [number, number][] = coordinates.map((point: any): [number, number] | null => {
            if (Array.isArray(point) && point.length >= 2 && 
                typeof point[0] === 'number' && typeof point[1] === 'number' &&
                !isNaN(point[0]) && !isNaN(point[1])) {
                return [point[1], point[0]]; // [lat, lon] from [lon, lat]
            }
            return null;
        }).filter((coord: [number, number] | null): coord is [number, number] => coord !== null);
        
        if (coords.length > 2) {
            try {
                const style = getParcelStyle(options, item);
                const polygon = L.polygon(coords, style);
                polygon.addTo(map);
            } catch (error) {
                console.error(`🗺️ [POLYGON] Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
            }
        } else {
            console.warn(`🗺️ [POLYGON] Insufficient coords: ${coords.length}`);
        }
    } else if (normalizedGeometry.type === 'MultiPolygon') {
        // Validate multipolygon coordinates structure
        if (!Array.isArray(normalizedGeometry.coordinates) || normalizedGeometry.coordinates.length === 0) {
            console.warn('🗺️ Invalid multipolygon coordinates array:', normalizedGeometry.coordinates);
            return;
        }
        
        normalizedGeometry.coordinates.forEach((polygonCoords: any, polyIndex: number) => {
            if (!Array.isArray(polygonCoords) || polygonCoords.length === 0) {
                console.warn(`🗺️ Invalid multipolygon coordinate array at index ${polyIndex}:`, polygonCoords);
                return;
            }
            
            const coordinates = polygonCoords[0];
            if (!Array.isArray(coordinates) || coordinates.length === 0) {
                console.warn(`🗺️ Invalid multipolygon coordinate array at index ${polyIndex}:`, coordinates);
                return;
            }
            
            const coords: [number, number][] = coordinates.map((point: any): [number, number] | null => {
                if (Array.isArray(point) && point.length >= 2 && 
                    typeof point[0] === 'number' && typeof point[1] === 'number' &&
                    !isNaN(point[0]) && !isNaN(point[1])) {
                    return [point[1], point[0]]; // [lat, lon] from [lon, lat]
                }
                return null;
            }).filter((coord: [number, number] | null): coord is [number, number] => coord !== null);
            
            if (coords.length > 2) {
                try {
                    const style = getParcelStyle(options, item);
                    const polygon = L.polygon(coords, style);
                    polygon.addTo(map);
                } catch (error) {
                    console.error(`🗺️ Error adding multipolygon ${polyIndex} to map:`, error, item);
                }
            } else {
                console.warn(`🗺️ Insufficient valid coordinates for multipolygon ${polyIndex}:`, coords.length);
            }
        });
    }
};

// Helper function to get icon URL for a POI type
const getIconUrl = (type: any) => {
    const t = String(type || '');
    const lowerT = t.toLowerCase();
    const cleanT = lowerT.replace(/[^a-z0-9]/g, '');
    
    // Try exact match first (enum value)
    if (t === PoIType.address || lowerT === 'address') return markerAddress;
    if (t === PoIType.chargingStation || lowerT === 'chargingpoint' || cleanT === 'chargingstation') return markerChargingStation;
    if (t === PoIType.university || lowerT === 'university') return markerUniversity;
    if (t === PoIType.school || lowerT === 'school') return markerSchool;
    if (t === PoIType.fireStation || cleanT === 'firestation') return markerFireStation;
    if (t === PoIType.advertisementColumn || cleanT === 'advertisementcolumn' || lowerT === 'advertisement-column') return markerAdvertisementColumn;
    if (t === PoIType.billboard || lowerT === 'billboard') return markerBillboard;
    if (t === PoIType.childCareFacility || cleanT === 'childcarefacility' || lowerT === 'child-care-facility' || lowerT === 'kindergarten') return markerChildCareFacility;
    if (t === PoIType.hospital || lowerT === 'hospital') return markerHospital;
    if (t === PoIType.pharmacy || lowerT === 'pharmacy') return markerPharmacy;
    if (t === PoIType.hotel || lowerT === 'hotel') return markerHotel;
    if (t === PoIType.restaurant || lowerT === 'restaurant') return markerRestaurant;
    if (t === PoIType.airport || lowerT === 'airport') return markerAirport;
    if (t === PoIType.policeStation || cleanT === 'policestation') return markerPoliceStation;
    if (t === PoIType.freewayMaintenanceOffice || cleanT === 'freewaymaintenanceoffice') return markerFreewayMaintenanceOffice;
    if (t === PoIType.parkingLot || lowerT === 'parkinglot') return markerParkingLot;
    if (t === PoIType.busStop || lowerT === 'busstop') return markerBusStop;
    if (t === PoIType.church || lowerT === 'church') return markerChurch;
    if (t === PoIType.hairdresser || lowerT === 'hairdresser') return markerHairdresser;
    if (t === PoIType.supermarket || lowerT === 'supermarket') return markerSupermarket;
    if (t === PoIType.doctor || lowerT === 'doctor') return markerDoctor;
    if (t === PoIType.parcelStation || lowerT === 'parcelstation') return markerParcelStation;
    if (t === PoIType.careFacility || lowerT === 'carefacility') return markerCareFacility;
    if (t === PoIType.trainStation || lowerT === 'trainstation') return markerTrainStation;
    if (t === PoIType.powerUnit || lowerT === 'powerunit') return markerPowerUnit;
    if (t === PoIType.cellularTower || lowerT === 'cellulartower') return markerCellularTower;
    if (cleanT === 'biomassunit') return markerBiomassUnit;
    if (cleanT === 'gasconsumerunit') return markerGasConsumerUnit;
    if (cleanT === 'gasproducerunit') return markerGasProducerUnit;
    if (cleanT === 'gasstorageunit') return markerGasStorageUnit;
    if (cleanT === 'geogrubengaspressurereliefunit') return markerGeoGrubengasPressureReliefUnit;
    if (cleanT === 'nuclearunit') return markerNuclearUnit;
    if (cleanT === 'solarunit') return markerSolarUnit;
    if (cleanT === 'substation') return markerSubstation;
    if (lowerT === 'department') return markerDepartment;
    if (cleanT === 'candidateproject') return markerCandidateProject;
    if (lowerT === 'company') return markerCompany;
    if (cleanT === 'pollutiondata') return markerPollution;
    
    // If it's a known non-POI layer element type, return school as fallback (like MapAndImage)
    if (['shape', 'way', 'circle', 'chart'].includes(lowerT)) return markerSchool;

    return markerSchool; // Use school icon as absolute fallback instead of blue drop
};

// Helper function to get border color for a POI type
const getBorderColor = (type: any) => {
    let borderColor = '#000';
    const cleanT = String(type || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    switch (cleanT) {
        case 'chargingstation':
        case 'chargingpoint':
        case 'biomassunit':
        case 'gasconsumerunit':
        case 'gasproducerunit':
        case 'gasstorageunit':
        case 'geogrubengaspressurereliefunit':
        case 'solarunit':
            borderColor = '#00AA00'; break;
        case 'substation':
        case 'powerunit':
            borderColor = '#AA0000'; break;
        case 'nuclearunit':
            borderColor = '#000'; break;
        case 'university':
        case 'school':
        case 'childcarefacility':
        case 'kindergarten':
        case 'pharmacy':
        case 'busstop':
        case 'church':
        case 'hairdresser':
        case 'doctor':
        case 'parcelstation':
        case 'trainstation':
        case 'cellulartower':
            borderColor = '#0000AA'; break;
        case 'hospital':
        case 'firestation':
            borderColor = '#AA0000'; break;
        case 'restaurant':
        case 'hotel':
        case 'supermarket':
            borderColor = '#ca03ca'; break;
        case 'billboard':
        case 'advertisementcolumn':
            borderColor = '#BBBBBB'; break;
        case 'department':
            borderColor = '#00695C'; break;
        case 'candidateproject':
            borderColor = '#C2185B'; break;
    }
    return borderColor;
};

// Helper function to add structured layer elements to map
const addLayerElementToMap = (element: any, map: any, _options: any) => {
    const { display } = element;
    let { entity } = element;
    if (!display) return;

    // Transform entity to proper type if _type is present
    if (entity && entity._type && typeof (entity as any).getTypeClass !== 'function') {
        try {
            const EntityClass = getEntityType(entity._type);
            if (EntityClass) {
                // Generic normalization: Ensure both legacy 'shape' and GeoJSON 'geometry' are present if possible
                if (!entity.shape && entity.geometry?.coordinates?.[0]) {
                    const coords = entity.geometry.type === 'Polygon' ? entity.geometry.coordinates[0] : entity.geometry.coordinates;
                    if (Array.isArray(coords)) {
                        entity.shape = coords.map((coord: any) => 
                            Array.isArray(coord) ? { lon: coord[0], lat: coord[1] } : coord
                        );
                    }
                } else if (!entity.geometry && entity.shape && Array.isArray(entity.shape)) {
                    entity.geometry = {
                        type: 'Polygon', // Default to Polygon for shapes
                        coordinates: [entity.shape.map((point: any) => [point.lon || point.lng, point.lat])]
                    };
                }

                entity = new (EntityClass as any)(entity);
            }
        } catch (e) {
            console.warn(`Failed to instantiate entity of type ${entity._type}:`, e);
        }
    }

    const poiType = display.poiType || (entity as any)?.poiType || (entity as any)?._type || display.type;
    const iconUrl = brandMarkerUrl(display.brandWikidata ?? (entity as any)?.brandWikidata) ?? getIconUrl(poiType);
    const borderColor = getBorderColor(poiType);

    // Fallback for display data from entity if missing
    if (entity) {
        if (!display.shape && (entity.shape || entity.geometry)) {
            display.shape = entity.shape || entity.geometry;
        }
        if (display.lat === undefined || display.lon === undefined) {
            // Try to get lat/lon from entity
            if (entity.lat !== undefined && entity.lon !== undefined) {
                display.lat = entity.lat;
                display.lon = entity.lon;
            } else {
                // Try to get center from shape/geometry
                const bounds = getBoundsForEntity(entity);
                if (bounds) {
                    const center = bounds.getCenter();
                    display.lat = center.lat;
                    display.lon = center.lng;
                }
            }
        }
    }

    const poiIcon = L.divIcon({
        className: 'marker-div-icon',
        html: `
            <div class="marker" style="width: 35px; height: 35px;">
                <span class="pin ${String(poiType || '').replace(/[^a-zA-Z0-9]/g, '')}" style="border: 0.5px solid ${borderColor};">
                    <img src="${iconUrl}" style="width: 25px; height: 25px;" />
                </span>
            </div>
        `,
        iconSize: [35, 35],
        iconAnchor: [17.5, -1.29],
        popupAnchor: [0, -35]
    });

    switch (display.type) {
        case 'shape':
        case 'way': {
            const geometry = display.shape?.geometry || display.shape;
            if (geometry) {
                const addPolygon = (coords: any) => {
                    if (!Array.isArray(coords) || coords.length === 0) return;

                    // Support both standard GeoJSON nested rings and flat array of points
                    const isFlat = Array.isArray(coords[0]) && typeof coords[0][0] === 'number';
                    const rings = isFlat ? [coords] : coords;

                    const latLngs = rings.map((ring: any) => {
                        if (!Array.isArray(ring)) return [];
                        return ring.map((coord: any) => {
                            if (Array.isArray(coord) && coord.length >= 2) {
                                return [coord[1], coord[0]];
                            } else if (coord && typeof coord.lat === 'number' && typeof coord.lon === 'number') {
                                return [coord.lat, coord.lon];
                            }
                            return null;
                        }).filter((c): c is [number, number] => c !== null);
                    }).filter((ring: any[]) => ring.length >= 3);

                    if (latLngs.length === 0) return;

                    L.polygon(latLngs, {
                        color: display.color || '#649aaa',
                        fillColor: display.fillColor || display.color || '#649aaa',
                        fillOpacity: display.fillOpacity ?? 0.3,
                        weight: display.weight ?? 1,
                        opacity: display.opacity ?? 0.8,
                    }).addTo(map);
                };

                const addPolyline = (coords: any) => {
                    if (!Array.isArray(coords) || coords.length === 0) return;

                    // Support both standard GeoJSON LineString and flat array of points
                    const isFlat = Array.isArray(coords[0]) && typeof coords[0][0] === 'number';
                    const lines = isFlat ? [coords] : (Array.isArray(coords[0]) ? coords : [coords]);

                    const allLinesLatLngs = lines.map((line: any) => {
                        if (!Array.isArray(line)) return [];
                        return line.map((coord: any) => {
                            if (Array.isArray(coord) && coord.length >= 2) {
                                return [coord[1], coord[0]];
                            } else if (coord && typeof coord.lat === 'number' && typeof coord.lon === 'number') {
                                return [coord.lat, coord.lon];
                            }
                            return null;
                        }).filter((c): c is [number, number] => c !== null);
                    }).filter((line: any[]) => line.length >= 2);

                    if (allLinesLatLngs.length === 0) return;

                    allLinesLatLngs.forEach(lineLatLngs => {
                        L.polyline(lineLatLngs, {
                            color: display.color || '#649aaa',
                            weight: display.weight ?? 2,
                            opacity: display.opacity ?? 0.8,
                        }).addTo(map);
                    });
                };

                if (geometry.type === 'Polygon') {
                    addPolygon(geometry.coordinates);
                } else if (geometry.type === 'MultiPolygon') {
                    geometry.coordinates.forEach((polyCoords: any) => addPolygon(polyCoords));
                } else if (geometry.type === 'LineString') {
                    addPolyline(geometry.coordinates);
                } else if (geometry.type === 'MultiLineString') {
                    geometry.coordinates.forEach((lineCoords: any) => addPolyline(lineCoords));
                }
            }

            // Also render a marker if we have coordinates
            if (display.lat !== undefined && display.lon !== undefined) {
                L.marker([display.lat, display.lon], { icon: poiIcon }).addTo(map);
            }
            break;
        }

        case 'poi': {
            if (display.lat === undefined || display.lon === undefined) return;
            L.marker([display.lat, display.lon], { icon: poiIcon }).addTo(map);
            break;
        }

        case 'circle': {
            if (display.lat === undefined || display.lon === undefined) return;
            L.circle([display.lat, display.lon], {
                radius: display.radius || 1000,
                color: display.color || '#ff0000',
                fillColor: display.fillColor || display.color || '#ff0000',
                fillOpacity: display.fillOpacity ?? 0.3,
                weight: display.weight ?? 2,
                opacity: display.opacity ?? 0.8,
            }).addTo(map);
            break;
        }

        case 'chart': {
            if (display.lat === undefined || display.lon === undefined) return;
            L.marker([display.lat, display.lon], { icon: poiIcon }).addTo(map);
            break;
        }
    }
};

// Extend Window interface to include our global state
declare global {
  interface Window {
    streamCompletionState?: {
      isStreamCompleted(): boolean;
      registerPendingResource(resourceId: string): void;
      markResourceCompleted(resourceId: string): void;
    };
    imageUploadTracker?: {
      registerUpload(resourceId: string, resourceType: string, uploadUrl?: string): void;
      updateStatus(resourceId: string, status: 'uploading' | 'success' | 'failed', finalUrl?: string, error?: string): void;
    };
  }
}

// The "Download map as PNG" button is hidden until its export is fixed. The handler
// (exportMapAsPNG) is deliberately left in place — flip this back to true to restore the button.
const SHOW_MAP_DOWNLOAD_BUTTON = false;

// Global state for expanded resources to persist across re-renders
const expandedResources = new globalThis.Map<string, boolean>();

interface MapResourceProps {
    resource: any;
    resourceSelected?: (resource: any) => void;
    mapResource?: any;
    authentication?: any;
    userAPI?: any;
    isExpanded?: boolean;
    onToggleExpanded?: () => void;
    stableKey?: string;
}

const MapResourceComponent = React.forwardRef<any, MapResourceProps>(({ 
    resource, 
    resourceSelected, 
    mapResource: propMapResource,
    authentication: propAuthentication,
    userAPI: propUserAPI,
    isExpanded: propIsExpanded,
    onToggleExpanded: propOnToggleExpanded,
    stableKey
}, ref) => {
    const _navigate = useNavigate();
    const [, setMapQuery] = useAtom(mapQueryAtom);
    const [, setMapData] = useAtom(mapDataAtom);
    const [, setMapOptions] = useAtom(mapOptionsAtom);
    const [, setSelectedEntity] = useAtom(selectedEntityAtom);
    
    const resourceKey = stableKey || resource?.reference || resource?._id;
    const [expanded, setExpanded] = useState(() => {
        const globalExpanded = expandedResources.get(resourceKey) || false;
        return propIsExpanded !== undefined ? propIsExpanded : globalExpanded;
    });
    
    const [authentication] = useAtom(authenticationAtom);
    const [mapResource, setMapResource] = useState(propMapResource || null) as any;
    const [imageGenerated, setImageGenerated] = useState(false);
    const mapRef = useRef<any>(null);
    const mapInstanceRef = useRef<any>(null);
    const eventListenersRegistered = useRef(false);
    
    // Use props if provided, otherwise use state
    const currentMapResource = propMapResource || mapResource;
    const currentAuthentication = propAuthentication || authentication;
    const currentUserAPI = propUserAPI || userAPI;
    const currentIsExpanded = propIsExpanded !== undefined ? propIsExpanded : expanded;
    const currentOnToggleExpanded = propOnToggleExpanded || ((e?: any) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        const newExpanded = !expanded;
        console.log('🖱️ MapResource toggle, key:', resourceKey, 'new expanded:', newExpanded);
        setExpanded(newExpanded);
        // Store in global state to persist across re-renders
        expandedResources.set(resourceKey, newExpanded);
    });
    
    // Track start time for shadow resource rendering
    const shadowStartTime = useRef<number | null>(null);
    
    // Expose ref methods
    React.useImperativeHandle(ref, () => ({
        getMap: () => mapInstanceRef.current
    }));
    const shadowResourceCreated = useRef(false);
    
    // Create shadow resource for streaming image generation
    useEffect(() => {
        if (currentMapResource && !imageGenerated && resource._streamingContext && !shadowResourceCreated.current) {
            // Double-check DOM to prevent multiple shadow resources
            const existingShadow = document.getElementById(`shadow-map-${currentMapResource._id}`);
            if (existingShadow) {
                return;
            }
            
            // Additional check: prevent shadow creation if resource already has an image
            if (currentMapResource.imageGenerated || currentMapResource.hasImage) {
                setImageGenerated(true);
                return;
            }
            
            shadowResourceCreated.current = true;
            shadowStartTime.current = Date.now();
            createShadowMapResource();
        }
    }, [currentMapResource?._id, resource._streamingContext]);
    
    // Create shadow map resource for streaming image generation
    const createShadowMapResource = () => {
        if (imageGenerated) return;
        
        // Prevent multiple shadow resources for the same resource
        const existingShadow = document.getElementById(`shadow-map-${currentMapResource._id}`);
        if (existingShadow) return;
        
        // Create shadow container
        const shadowContainer = document.createElement('div');
        shadowContainer.id = `shadow-map-${currentMapResource._id}`;
        shadowContainer.style.position = 'absolute';
        shadowContainer.style.left = '-9999px';
        shadowContainer.style.top = '-9999px';
        shadowContainer.style.width = '800px';
        shadowContainer.style.height = '600px';
        shadowContainer.style.visibility = 'visible'; // Make visible for map rendering
        shadowContainer.style.opacity = '1'; // Make fully visible for map rendering
        shadowContainer.style.zIndex = '-1';
        document.body.appendChild(shadowContainer);
        
        // Create shadow MapResource component (full component, not just Leaflet)
        const tempMapRef = React.createRef<any>();
        const root = ReactDOM.createRoot(shadowContainer);
        root.render(
            <BrowserRouter>
                <MapResource
                    ref={tempMapRef}
                    resource={{...resource, _streamingContext: true}} // Pass streaming context to shadow component
                    mapResource={currentMapResource}
                    authentication={currentAuthentication}
                    userAPI={currentUserAPI}
                    isExpanded={true} // Force expanded for shadow
                    onToggleExpanded={() => {}} // No-op for shadow
                />
            </BrowserRouter>
        );
        
        // Shadow map created - events will be handled via Map component ref callback
        
        // Hide the shadow container after map has time to render
        setTimeout(() => {
            shadowContainer.style.opacity = '0';
        }, 2000);
    };

    // All useEffect hooks must be called before any early returns
    useEffect(() => {
        const call = async () => {
            let newResource: any = null;

            // Load resource from reference
            if (resource.reference) {
                newResource = await userAPI.getResource(authentication.customer, resource.reference);
            } else {
                newResource = resource;
            }

            // Ensure data is always an object, never null or undefined
            if (!newResource.data) {
                newResource.data = {};
            }

            // Ensure options is always an object, never null or undefined
            if (!newResource.options) {
                newResource.options = {};
            }

            // Convert data to proper model instances
            if (newResource.data.plotsOfLand) {
                // Convert plotsOfLand to parcels for consistency
                if (!newResource.data.parcels) {
                    newResource.data.parcels = [];
                }
                newResource.data.plotsOfLand.forEach((plot: any) => {
                    // Convert PlotOfLand with shape to Parcel with geometry
                    const parcel = {
                        ...plot,
                        _type: 'Parcel',
                        geometry: plot.shape ? {
                            type: 'Polygon',
                            coordinates: [plot.shape.map((point: any) => [point.lon, point.lat])]
                        } : plot.geometry
                    };
                    newResource.data.parcels.push(parcel);
                });
                // Remove plotsOfLand after conversion
                delete newResource.data.plotsOfLand;
            }

            if (newResource.data.buildings) {
                // Don't convert fitted shapes to Building objects - they need to keep their geometry
                newResource.data.buildings = newResource.data.buildings.map((properties: any) => {
                    // If it's a fitted shape, keep it as-is to preserve geometry
                    if (properties.type === 'fittedShape' || properties.landUse === 'Building') {
                        return properties; // Keep original structure for fitted shapes
                    }
                    
                    // For regular buildings, convert to Building model
                    const requiredFireExtinguishers = properties.requiredFireExtinguishers;
                    const building = new Building(properties);
                    (building as any).requiredFireExtinguishers = requiredFireExtinguishers;
                    return building;
                });
            }

            if (newResource.data.localGovernments) {
                newResource.data.localGovernments = newResource.data.localGovernments.map((properties: any) => new LocalGovernment(properties));
            }

            if (newResource.data.areas) {
                newResource.data.areas = newResource.data.areas.map((properties: any) => new Area(properties));
            }

            // Instantiate entities in the new layers structure
            if (newResource.data.layers) {
                Object.values(newResource.data.layers).forEach((layer: any) => {
                    if (layer.elements) {
                        layer.elements.forEach((element: any) => {
                            if (element.entity && element.entity._type && typeof element.entity.getTypeClass !== 'function') {
                                try {
                                    const EntityClass = getEntityType(element.entity._type);
                                    if (EntityClass) {
                                        element.entity = new (EntityClass as any)(element.entity);
                                    }
                                } catch (e) {
                                    // Ignore if type is not registered, keep as plain object
                                    console.warn(`[MapResource] Could not instantiate entity of type ${element.entity._type}:`, e);
                                }
                            }
                        });
                    }
                });
            }

            setMapResource(newResource);
        }

        // Only call if we have a reference or if we don't have local data yet
        if (resource.reference || !mapResource) {
            call();
        }
    }, [resource.reference, resource._id, authentication.customer]);

    // Export map as PNG using shared rendering logic
    const exportMapAsPNG = async () => {
        if (!mapResource) {
            console.warn('❌ [DOWNLOAD] Map resource not available for export');
            return;
        }
        
        try {
            const result = await renderMapForCapture(mapResource);
            if (!result) {
                console.error('❌ [DOWNLOAD] Failed to render Map for capture');
                return;
            }
            
            const { dataURL, cleanup } = result;
            
            // Download the image to user's computer
            console.log('🧪 [DOWNLOAD] Creating download link for local save...');
            const downloadLink = document.createElement('a');
            downloadLink.href = dataURL;
            downloadLink.download = `${mapResource?.title || 'map'}.png`;
            document.body.appendChild(downloadLink);
            downloadLink.click();
            document.body.removeChild(downloadLink);
            
            console.log('✅ [DOWNLOAD] Map download triggered successfully!');
            cleanup();
            
        } catch (error) {
            console.error('❌ [DOWNLOAD] Error exporting map:', error);
        }
    };

    // Generate and store map image in background with 3-attempt retry logic
    const generateAndStoreImage = async (retryCount = 0) => {
        if (!mapResource || !mapResource._id || imageGenerated) {
            return;
        }

        // Check if image already exists for this resource
        if (mapResource.imageGenerated || mapResource.hasImage) {
            setImageGenerated(true);
            return;
        }

        // Register upload in tracker
        if (window.imageUploadTracker) {
            window.imageUploadTracker.registerUpload(mapResource._id, 'map');
        }

        try {
            // Use the exact same proven rendering logic as download
            const result = await renderMapForCapture(mapResource);
            if (!result) {
                throw new Error('Failed to render Map for capture');
            }
            
            const { dataURL, cleanup } = result;
            
            // Update status to uploading
            if (window.imageUploadTracker) {
                window.imageUploadTracker.updateStatus(mapResource._id, 'uploading');
            }

            // Store image on server asynchronously
            const storeResult = await userAPI.storeResourceImage(authentication.customer, 'map', mapResource._id, dataURL);
            
            if (storeResult && storeResult.success) {
                // Update status to success - use production API URL with correct /documents/ endpoint format
                const finalUrl = `${configuration.productionAPIURL}/documents/${authentication.customer}/resource-images/map/${mapResource._id}.png`;
                if (window.imageUploadTracker) {
                    window.imageUploadTracker.updateStatus(mapResource._id, 'success', finalUrl);
                }
                const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                console.log(`✅ Map upload successful: ${mapResource._id} -> ${finalUrl} (${elapsedTime}ms)`);
            } else {
                // Update status to failed
                if (window.imageUploadTracker) {
                    window.imageUploadTracker.updateStatus(mapResource._id, 'failed', undefined, storeResult?.error || 'Upload failed');
                }
                console.error(`❌ Map upload failed: ${mapResource._id}`, storeResult?.error);
            }
            
            setImageGenerated(true);
            
            // Clean up rendering resources
            cleanup();
            
        } catch (error) {
            // Retry logic for generation - 3 attempts maximum
            if (retryCount < 2) {  // 0, 1, 2 = 3 attempts total
                console.log(`🗺️ Retrying map image generation in 2 seconds (attempt ${retryCount + 1}/3 failed)`);
                setTimeout(() => {
                    generateAndStoreImage(retryCount + 1);
                }, 2000);
            } else {
                console.error(`❌ Map upload failed after 3 retries: ${mapResource._id}`);
                
                // Update status to failed
                if (window.imageUploadTracker) {
                    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
                    window.imageUploadTracker.updateStatus(mapResource._id, 'failed', undefined, errorMessage);
                }
            }
        }
    };


    return <div className="box-border w-full">
        {/* Die Karte misst beim Aufbau ihren Container; in einer zusammen-
            geklappten Zeile stünde sie auf 0 Pixel Höhe. Deshalb hier weiter
            bedingtes Rendern statt der Grid-Animation — nur Kopfzeile und
            Chevron folgen dem gemeinsamen Muster. */}
        <ResourceDisclosureHeader
            expanded={currentIsExpanded}
            onToggle={(e) => currentOnToggleExpanded(e)}
            testId="chat-resource-map-toggle"
            icon={<MapPin aria-hidden className="h-5 w-5 shrink-0" />}
            title={currentMapResource?.title}
            actions={
            <div className="flex shrink-0 justify-end">
                {SHOW_MAP_DOWNLOAD_BUTTON && currentMapResource && (
                    <Button
                        variant="ghost"
                        size="icon"
                        type="button"
                        data-testid="chat-resource-map-download"
                        className="mr-0.5 h-8 w-8"
                        onClick={exportMapAsPNG}
                        title="Download map as PNG"
                    >
                        <Download size={16} />
                    </Button>
                )}
                <Button
                    variant="ghost"
                    size="icon"
                    type="button"
                    data-testid="chat-resource-map-expand"
                    className="h-8 w-8"
                    title="Expand"
                    aria-label="Expand"
                    onClick={() => {
                        setMapData(currentMapResource.data);
                        setMapQuery(currentMapResource.query);
                        setMapOptions(currentMapResource.options || {});
                        resourceSelected?.(currentMapResource);
                        //navigate("/map");
                        //window.open("/map", '_blank')
                    }}
                >
                    <Maximize2 size={16} />
                </Button>
            </div>
            }
        />
                {currentIsExpanded && currentMapResource && currentMapResource.data
                    ?
                    <div className="mt-[25px]">
                        <Map ref={(ref) => {
                            mapRef.current = ref;
                            if (ref && ref.getMap) {
                                mapInstanceRef.current = ref.getMap();
                                
                                        // Add event listeners for shadow components (streaming context)
                                        if (resource._streamingContext && !imageGenerated && mapInstanceRef.current && !eventListenersRegistered.current) {
                                            eventListenersRegistered.current = true;
                                    let uploadTriggered = false;
                                    // Try multiple events to catch when map is ready
                                    const handleMapReady = () => {
                                        if (!uploadTriggered && !imageGenerated) {
                                            uploadTriggered = true;
                                            const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                                            console.log(`🗺️ Map image submitted: ${currentMapResource._id} -> ${elapsedTime}ms`);
                                            generateAndStoreImage();
                                            setImageGenerated(true);
                                            // Purge shadow resource after upload
                                            setTimeout(() => {
                                                const shadowContainer = document.getElementById(`shadow-map-${currentMapResource._id}`);
                                                if (shadowContainer && shadowContainer.parentNode) {
                                                    shadowContainer.parentNode.removeChild(shadowContainer);
                                                }
                                            }, 1000);
                                        }
                                    };
                                    
                                    // Register multiple events to catch when map is ready
                                    mapInstanceRef.current.on('load', handleMapReady);
                                    mapInstanceRef.current.on('tileload', handleMapReady);
                                    mapInstanceRef.current.on('zoomend', handleMapReady);
                                    
                                        // Also try a timeout fallback
                                        setTimeout(() => {
                                            if (!uploadTriggered && !imageGenerated) {
                                                handleMapReady();
                                            }
                                        }, 3000);
                                } else if (resource._streamingContext && !imageGenerated && !mapInstanceRef.current && !eventListenersRegistered.current) {
                                    // Map instance not ready yet, try again after a short delay
                                    setTimeout(() => {
                                        if (mapInstanceRef.current && !imageGenerated && !eventListenersRegistered.current) {
                                            eventListenersRegistered.current = true;
                                            let uploadTriggered = false;
                                            mapInstanceRef.current.on('load', () => {
                                                if (!uploadTriggered && !imageGenerated) {
                                                    uploadTriggered = true;
                                                    const elapsedTime = shadowStartTime.current ? Date.now() - shadowStartTime.current : 0;
                                                    console.log(`🗺️ Map image submitted: ${currentMapResource._id} -> ${elapsedTime}ms`);
                                                    generateAndStoreImage();
                                                    setImageGenerated(true);
                                                    // Purge shadow resource after upload
                                                    setTimeout(() => {
                                                        const shadowContainer = document.getElementById(`shadow-map-${currentMapResource._id}`);
                                                        if (shadowContainer && shadowContainer.parentNode) {
                                                            shadowContainer.parentNode.removeChild(shadowContainer);
                                                        }
                                                    }, 1000);
                                                }
                                            });
                                        }
                                    }, 1000);
                                }
                            }
                        }} height={800} requireModifierForScrollZoom={true} objectClass={currentMapResource.query?.objectClass} data={currentMapResource.data} query={currentMapResource.query} options={currentMapResource.options || {}}
                            setObject={(entity: any) => {
                                // Update selection state but don't switch view to MapView
                                setSelectedEntity(entity);
                            }}></Map>
                    </div>
                    :
                    <></>
                }
        
    </div>
});

// Export with memoization but include data and options in the check
export const MapResource = React.memo(MapResourceComponent, (prevProps, nextProps) => {
    // Custom comparison function for memoization
    return (
        prevProps.stableKey === nextProps.stableKey &&
        prevProps.resource?.reference === nextProps.resource?.reference &&
        prevProps.resource?._id === nextProps.resource?._id &&
        prevProps.isExpanded === nextProps.isExpanded &&
        prevProps.mapResource === nextProps.mapResource &&
        // Also check data and options for changes to ensure the map updates
        prevProps.resource?.data === nextProps.resource?.data &&
        prevProps.resource?.options === nextProps.resource?.options
    );
});
